import { Injectable } from '@angular/core';

/**
 * SecureStorageService provides memory-only storage for sensitive data.
 * Unlike sessionStorage/localStorage, data stored here:
 * - Is NOT accessible via JavaScript XSS attacks (stored in closure)
 * - Is NOT persisted to disk
 * - Is cleared when the browser tab is closed
 * 
 * Trade-off: Data is lost on page refresh. For tokens that need persistence,
 * consider using httpOnly cookies set by the server.
 */
@Injectable({
  providedIn: 'root'
})
export class SecureStorageService {
  // Private closure - not accessible from browser dev tools or XSS
  private secureStore: Map<string, string> = new Map<string, string>();

  /**
   * Store a value securely in memory
   * @param key - Storage key
   * @param value - Value to store (will be stored as-is)
   */
  setItem(key: string, value: string): void {
    if (!key) {
      return;
    }
    this.secureStore.set(key, value);
  }

  /**
   * Retrieve a value from secure storage
   * @param key - Storage key
   * @returns The stored value or null if not found
   */
  getItem(key: string): string | null {
    return this.secureStore.get(key) ?? null;
  }

  /**
   * Remove an item from secure storage
   * @param key - Storage key to remove
   */
  removeItem(key: string): void {
    this.secureStore.delete(key);
  }

  /**
   * Check if a key exists in secure storage
   * @param key - Storage key to check
   * @returns true if the key exists
   */
  hasItem(key: string): boolean {
    return this.secureStore.has(key);
  }

  /**
   * Clear all stored values
   */
  clear(): void {
    this.secureStore.clear();
  }

  /**
   * Store an object as JSON
   * @param key - Storage key
   * @param value - Object to store
   */
  setObject<T>(key: string, value: T): void {
    this.setItem(key, JSON.stringify(value));
  }

  /**
   * Retrieve and parse a JSON object
   * @param key - Storage key
   * @returns Parsed object or null if not found
   */
  getObject<T>(key: string): T | null {
    const item: string | null = this.getItem(key);
    if (!item) {
      return null;
    }
    try {
      return JSON.parse(item) as T;
    } catch {
      return null;
    }
  }
}
